import {
  filterDirectInviteCarrierACLs,
  getScheduleTypeOrDefaultForPoint,
} from "utils";

import type { ShipmentTemplateContent } from "types/shipment";

function getTemplateRoute(route: Route): ShipmentTemplateRoutePoint[] {
  return route.route_points.map((p: RoutePoint) => ({
    point: {
      isBeingEdited: true,
      stop_type: p.stop_type,
      stop_operation: p.stop_operation,
      start_time: p.start_time,
      point_number: p.point_number,
      schedule_type: getScheduleTypeOrDefaultForPoint(p),
      point_location: {
        city: p.point_location.city,
        country: p.point_location.country,
        province: p.point_location.province,
        postal: p.point_location.postal,
        longitude: p.point_location.longitude,
        latitude: p.point_location.latitude,
        address: p.point_location.address,
        created_at: p.point_location.created_at,
        business_name: p.point_location?.business_name ?? null,
      },
    },
    point_location: {
      city: p.point_location.city,
      country: p.point_location.country,
      province: p.point_location.province,
      postal: p.point_location.postal,
      longitude: p.point_location.longitude,
      latitude: p.point_location.latitude,
      address: p.point_location.address,
      created_at: p.point_location.created_at,
      business_name: p.point_location?.business_name ?? null,
    },
  }));
}

function getTemplateCustomerSnapshots(
  supplierShipmentInfo: SupplierShipmentCustomerInfo,
): TemplateCustomerSnapshot[] {
  const internalSnapshots: TemplateCustomerSnapshot[] = (
    supplierShipmentInfo.customer_roles ?? []
  ).map((role: SupplierShipmentCustomerRole) => ({
    type: "internal_customer",
    role_id: role.id,
    email: role.user.email,
    first_name: role.user.first_name,
    last_name: role.user.last_name,
    company_name: role.company.name,
  }));

  const externalSnapshots: TemplateCustomerSnapshot[] = (
    supplierShipmentInfo.external_customers ?? []
  ).map((customer: ExternalCustomerInfo) => ({
    type: "external_customer",
    id: customer.id,
    email: customer.email,
    first_name: customer.first_name,
    last_name: customer.last_name,
    company_name: customer.company_name,
  }));

  return [...internalSnapshots, ...externalSnapshots];
}

export function geCreateTemplateBodyFromCondensedShipment(
  shipment: ShipperCondensedShipment,
  templateName: string,
  route: Route | null,
): ShipmentTemplateContent {
  /*
  Creates a ShipmentTemplateContent (the body required to create a shipment) from an existing
  ShipperCondensedShipment.
  */
  return {
    shipment: {
      footage: shipment?.info?.footage ?? null,
      service: shipment?.info?.service ?? null,
      currency: null,
      type:
        shipment?.info?.type &&
        shipment?.info?.type !== "MULTI_SHIPMENT_PROJECT_CHILD"
          ? shipment?.info?.type
          : "SINGLE",
      commodity: shipment?.info?.commodity?.name ?? null,
      weight_lb: shipment?.info?.weight_lb ?? null,
      preferred_price: shipment?.info?.preferred_price ?? null,
      additional_details: shipment?.info?.additional_details ?? null,
      commodity_freetext: shipment?.info?.commodity_freetext ?? null,
      special_requirements: shipment?.info?.special_requirements ?? null,
      book_now_currency: shipment?.book_now_info?.book_now_currency ?? null,
      book_now_price: shipment?.book_now_info?.book_now_price ?? null,
      quoting_unit_of_measure: shipment?.info?.quoting_unit_of_measure ?? null,
      custom_name: shipment?.info?.custom_name ?? null,
      analytics: null,
    },
    template_name: templateName,
    equipment_types:
      shipment?.equipments.map((equipment: Equipment) => equipment.type) ?? [],
    notification_settings_emails: filterDirectInviteCarrierACLs(
      shipment.carrier_acl,
    ).map((acl) => acl.role.user.email),
    notification_settings_type:
      shipment.network_notification_type ?? "ALL_CARRIER_NETWORK",
    route: route != null ? getTemplateRoute(route) : [],
    sharee_role_ids: shipment?.sharees
      ? shipment.sharees.map((sharee: ShipmentSharee) => sharee.sharee.id)
      : [],
    project:
      shipment?.info?.type && shipment.info.type === "MULTI_SHIPMENT_PROJECT"
        ? {
            loads_available: shipment?.project?.loads_available ?? null,
          }
        : null,
    notification_settings_partner_groups:
      shipment.network_groups.map((group) => group.id) ?? [],
    supplier_shipment_info:
      shipment?.supplier_shipment_info?.customer_roles?.length ||
      shipment?.supplier_shipment_info?.external_customers?.length
        ? {
            customer_role_ids:
              shipment.supplier_shipment_info?.customer_roles?.map(
                (role: SupplierShipmentCustomerRole) => role.id,
              ) ?? [],
            external_customer_ids:
              shipment.supplier_shipment_info?.external_customers?.map(
                (customer: ExternalCustomerInfo) => customer.id,
              ) ?? [],
            // Denormalized so the template still renders this customer after
            // it's removed from the network (see #2836) -- live network data
            // stays authoritative whenever an id still resolves against it.
            customer_snapshots: getTemplateCustomerSnapshots(
              shipment.supplier_shipment_info,
            ),
          }
        : null,
  };
}
